import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Music, ListMusic, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from '@/hooks/use-toast';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Company } from '@/types/company';
import { Playlist } from '@/types/playlist';

type CompanySortOption = 'name-asc' | 'name-desc' | 'playlist-desc' | 'playlist-asc';
type PlaylistSortOption = 'name-asc' | 'name-desc' | 'songs-desc' | 'songs-asc' | 'companies-desc' | 'companies-asc';

interface BulkAssignPlaylistsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function BulkAssignPlaylistsModal({
  isOpen,
  onClose,
  onSuccess
}: BulkAssignPlaylistsModalProps) {
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const [selectedPlaylists, setSelectedPlaylists] = useState<Playlist[]>([]);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [playlistSearchTerm, setPlaylistSearchTerm] = useState("");
  const [companySortBy, setCompanySortBy] = useState<CompanySortOption>('name-asc');
  const [playlistSortBy, setPlaylistSortBy] = useState<PlaylistSortOption>('name-asc');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Modal kapandığında state'leri sıfırla
  useEffect(() => {
    if (!isOpen) {
      setSelectedCompanies([]);
      setSelectedPlaylists([]);
      setCompanySearchTerm("");
      setPlaylistSearchTerm("");
    }
  }, [isOpen]);

  // Firmaları getir
  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ['companies'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/companies`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Firma verileri alınamadı');
      }

      return response.json();
    }
  });

  // Playlistleri getir
  const { data: playlistsData } = useQuery({
    queryKey: ['playlists-all'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/playlists?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Playlist verileri alınamadı');
      }

      return response.json();
    }
  });

  const playlists: Playlist[] = playlistsData?.playlists || playlistsData || [];

  // Firma seçildiğinde, o firmalara atanmış playlistleri otomatik seç
  useEffect(() => {
    if (selectedCompanies.length > 0 && playlists.length > 0) {
      // Seçili firmaların tüm playlist ID'lerini topla
      const allAssignedPlaylistIds = new Set<string>();

      selectedCompanies.forEach(company => {
        if (company.playlists && Array.isArray(company.playlists)) {
          company.playlists.forEach(playlistId => {
            // playlistId string veya object olabilir
            const id = typeof playlistId === 'string' ? playlistId : (playlistId as any)?._id;
            if (id) allAssignedPlaylistIds.add(id);
          });
        }
      });

      // Bu ID'lere sahip playlist objelerini bul ve seç
      const assignedPlaylists = playlists.filter((playlist: Playlist) =>
        allAssignedPlaylistIds.has(playlist._id)
      );

      setSelectedPlaylists(assignedPlaylists);
    } else if (selectedCompanies.length === 0) {
      // Hiç firma seçili değilse playlistleri temizle
      setSelectedPlaylists([]);
    }
  }, [selectedCompanies, playlists]);

  const handleSubmit = async () => {
    if (selectedCompanies.length === 0 || selectedPlaylists.length === 0) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Lütfen en az bir firma ve bir playlist seçin.",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/playlists/bulk-assign`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            companies: selectedCompanies.map(c => c._id),
            playlists: selectedPlaylists.map(p => p._id)
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Toplu atama işlemi başarısız');
      }

      const result = await response.json();

      toast({
        title: "Başarılı",
        description: result.message || `${selectedPlaylists.length} playlist ${selectedCompanies.length} firmaya atandı.`,
      });

      onSuccess();
      onClose();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: error instanceof Error ? error.message : "Toplu atama işlemi sırasında bir hata oluştu.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sıralama fonksiyonları
  const sortCompanies = (list: Company[]) => {
    return [...list].sort((a, b) => {
      switch (companySortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name, 'tr');
        case 'name-desc':
          return b.name.localeCompare(a.name, 'tr');
        case 'playlist-desc':
          return (b.playlists?.length || 0) - (a.playlists?.length || 0);
        case 'playlist-asc':
          return (a.playlists?.length || 0) - (b.playlists?.length || 0);
        default:
          return 0;
      }
    });
  };

  const sortPlaylists = (list: Playlist[]) => {
    return [...list].sort((a, b) => {
      switch (playlistSortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name, 'tr');
        case 'name-desc':
          return b.name.localeCompare(a.name, 'tr');
        case 'songs-desc':
          return (b.songs?.length || 0) - (a.songs?.length || 0);
        case 'songs-asc':
          return (a.songs?.length || 0) - (b.songs?.length || 0);
        case 'companies-desc':
          return (b.companies?.length || 0) - (a.companies?.length || 0);
        case 'companies-asc':
          return (a.companies?.length || 0) - (b.companies?.length || 0);
        default:
          return 0;
      }
    });
  };

  // Filtrelenmiş ve sıralanmış listeler
  const filteredCompanies = useMemo(() => {
    const filtered = companies.filter(company =>
      company.name.toLowerCase().includes(companySearchTerm.toLowerCase())
    );
    return sortCompanies(filtered);
  }, [companies, companySearchTerm, companySortBy]);

  const filteredPlaylists = useMemo(() => {
    const filtered = playlists.filter((playlist: Playlist) =>
      playlist.name.toLowerCase().includes(playlistSearchTerm.toLowerCase())
    );
    return sortPlaylists(filtered);
  }, [playlists, playlistSearchTerm, playlistSortBy]);

  // Firma seçim fonksiyonları
  const toggleCompany = (company: Company) => {
    setSelectedCompanies(prev =>
      prev.some(c => c._id === company._id)
        ? prev.filter(c => c._id !== company._id)
        : [...prev, company]
    );
  };

  const isCompanySelected = (company: Company) => {
    return selectedCompanies.some(c => c._id === company._id);
  };

  const selectAllCompanies = () => {
    const targetCompanies = companySearchTerm ? filteredCompanies : companies;
    const allSelected = targetCompanies.every((company: Company) =>
      selectedCompanies.some(c => c._id === company._id)
    );

    if (allSelected) {
      const targetIds = targetCompanies.map((c: Company) => c._id);
      setSelectedCompanies(prev => prev.filter(c => !targetIds.includes(c._id)));
    } else {
      const newCompanies = targetCompanies.filter(
        (company: Company) => !selectedCompanies.some(c => c._id === company._id)
      );
      setSelectedCompanies(prev => [...prev, ...newCompanies]);
    }
  };

  // Playlist seçim fonksiyonları
  const togglePlaylist = (playlist: Playlist) => {
    setSelectedPlaylists(prev =>
      prev.some(p => p._id === playlist._id)
        ? prev.filter(p => p._id !== playlist._id)
        : [...prev, playlist]
    );
  };

  const isPlaylistSelected = (playlist: Playlist) => {
    return selectedPlaylists.some(p => p._id === playlist._id);
  };

  const selectAllPlaylists = () => {
    const targetPlaylists = playlistSearchTerm ? filteredPlaylists : playlists;
    const allSelected = targetPlaylists.every((playlist: Playlist) =>
      selectedPlaylists.some(p => p._id === playlist._id)
    );

    if (allSelected) {
      const targetIds = targetPlaylists.map((p: Playlist) => p._id);
      setSelectedPlaylists(prev => prev.filter(p => !targetIds.includes(p._id)));
    } else {
      const newPlaylists = targetPlaylists.filter(
        (playlist: Playlist) => !selectedPlaylists.some(p => p._id === playlist._id)
      );
      setSelectedPlaylists(prev => [...prev, ...newPlaylists]);
    }
  };

  // Tümü butonlarının label'ları
  const getCompanyButtonLabel = () => {
    const targetCompanies = companySearchTerm ? filteredCompanies : companies;
    const allSelected = targetCompanies.every((company: Company) =>
      selectedCompanies.some(c => c._id === company._id)
    );
    return allSelected ? 'Temizle' : 'Tümü';
  };

  const getPlaylistButtonLabel = () => {
    const targetPlaylists = playlistSearchTerm ? filteredPlaylists : playlists;
    const allSelected = targetPlaylists.every((playlist: Playlist) =>
      selectedPlaylists.some(p => p._id === playlist._id)
    );
    return allSelected ? 'Temizle' : 'Tümü';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>Toplu Playlist Atama</DialogTitle>
          <DialogDescription>
            Seçtiğiniz playlistleri birden fazla firmaya aynı anda atayın.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 py-4">
          {/* Sol Panel - Firma Seçimi */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Firmalar</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Firma ara..."
                value={companySearchTerm}
                onChange={(e) => setCompanySearchTerm(e.target.value)}
                className="flex-1"
              />
              <Button
                variant="outline"
                onClick={selectAllCompanies}
                type="button"
                size="sm"
              >
                {getCompanyButtonLabel()}
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {selectedCompanies.length} firma seçildi
              </div>
              <Select value={companySortBy} onValueChange={(v) => setCompanySortBy(v as CompanySortOption)}>
                <SelectTrigger className="w-[140px] h-8 text-xs">
                  <SelectValue placeholder="Sırala" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name-asc">A-Z</SelectItem>
                  <SelectItem value="name-desc">Z-A</SelectItem>
                  <SelectItem value="playlist-desc">Playlist (Çok)</SelectItem>
                  <SelectItem value="playlist-asc">Playlist (Az)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ScrollArea className="h-[350px] border rounded-lg p-2">
              <div className="space-y-1">
                {filteredCompanies.map((company) => {
                  const assignedPlaylistCount = company.playlists?.length || 0;
                  return (
                    <div
                      key={company._id}
                      onClick={() => toggleCompany(company)}
                      className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer hover:bg-accent transition-colors ${
                        isCompanySelected(company) ? 'bg-accent border border-primary' : 'border border-transparent'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate max-w-[180px]" title={company.name}>
                            {company.name.length > 25 ? company.name.substring(0, 25) + '...' : company.name}
                          </span>
                          {assignedPlaylistCount > 0 && (
                            <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 flex-shrink-0">
                              <ListMusic className="h-3 w-3 mr-1" />
                              {assignedPlaylistCount}
                            </Badge>
                          )}
                        </div>
                        {company.city && (
                          <p className="text-xs text-muted-foreground">{company.city}</p>
                        )}
                      </div>
                      {isCompanySelected(company) && (
                        <Check className="h-4 w-4 text-primary flex-shrink-0 ml-2" />
                      )}
                    </div>
                  );
                })}
                {filteredCompanies.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-4">
                    Firma bulunamadı
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Sağ Panel - Playlist Seçimi */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Playlistler</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Playlist ara..."
                value={playlistSearchTerm}
                onChange={(e) => setPlaylistSearchTerm(e.target.value)}
                className="flex-1"
              />
              <Button
                variant="outline"
                onClick={selectAllPlaylists}
                type="button"
                size="sm"
              >
                {getPlaylistButtonLabel()}
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {selectedPlaylists.length} playlist seçildi
              </div>
              <Select value={playlistSortBy} onValueChange={(v) => setPlaylistSortBy(v as PlaylistSortOption)}>
                <SelectTrigger className="w-[140px] h-8 text-xs">
                  <SelectValue placeholder="Sırala" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name-asc">A-Z</SelectItem>
                  <SelectItem value="name-desc">Z-A</SelectItem>
                  <SelectItem value="songs-desc">Şarkı (Çok)</SelectItem>
                  <SelectItem value="songs-asc">Şarkı (Az)</SelectItem>
                  <SelectItem value="companies-desc">Firma (Çok)</SelectItem>
                  <SelectItem value="companies-asc">Firma (Az)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ScrollArea className="h-[350px] border rounded-lg p-2">
              <div className="space-y-1">
                {filteredPlaylists.map((playlist: Playlist) => {
                  const assignedCompanyCount = playlist.companies?.length || 0;
                  return (
                    <div
                      key={playlist._id}
                      onClick={() => togglePlaylist(playlist)}
                      className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer hover:bg-accent transition-colors ${
                        isPlaylistSelected(playlist) ? 'bg-accent border border-primary' : 'border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {playlist.artwork ? (
                          <img
                            src={playlist.artwork.startsWith('https://') ? playlist.artwork : `https://${playlist.artwork}`}
                            alt={playlist.name}
                            className="h-10 w-10 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded bg-slate-200 flex items-center justify-center flex-shrink-0">
                            <Music className="h-5 w-5 text-slate-400" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate max-w-[180px]" title={playlist.name}>
                              {playlist.name.length > 25 ? playlist.name.substring(0, 25) + '...' : playlist.name}
                            </span>
                            {assignedCompanyCount > 0 && (
                              <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 bg-blue-100 text-blue-700 hover:bg-blue-100 flex-shrink-0">
                                <Building2 className="h-3 w-3 mr-1" />
                                {assignedCompanyCount}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {playlist.songs?.length || 0} şarkı
                          </p>
                        </div>
                      </div>
                      {isPlaylistSelected(playlist) && (
                        <Check className="h-4 w-4 text-primary flex-shrink-0 ml-2" />
                      )}
                    </div>
                  );
                })}
                {filteredPlaylists.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-4">
                    Playlist bulunamadı
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            İptal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || selectedCompanies.length === 0 || selectedPlaylists.length === 0}
          >
            {isSubmitting
              ? 'Atanıyor...'
              : `${selectedPlaylists.length} Playlist'i ${selectedCompanies.length} Firmaya Ata`
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
